import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { isEqual } from "lodash-es";

import { ControlCoveredPeriod } from "api/generated/model";

export type CoveredPeriodKey =
    | "None"
    | "CurrentMonth"
    | "PreviousMonth"
    | "TwoMonthsBeforeDueDate"
    | "ThreeMonthsBeforeDueDate"
    | "CurrentPeriod"
    | "CurrentQuarter"
    | "PreviousQuarter"
    | "CurrentHalfYear"
    | "PreviousHalfYear"
    | "CurrentYear"
    | "PreviousYear"
    | "NextYear";

export const COVERED_PERIOD_VALUES: Record<
    CoveredPeriodKey,
    ControlCoveredPeriod | null
> = {
    None: null,
    CurrentMonth: { data: 0, type: "Month" },
    PreviousMonth: { data: -1, type: "Month" },
    TwoMonthsBeforeDueDate: { data: -2, type: "Month" },
    ThreeMonthsBeforeDueDate: { data: -3, type: "Month" },
    CurrentPeriod: { data: 0, type: "TwoMonths" },
    CurrentQuarter: { data: 0, type: "Quarter" },
    PreviousQuarter: { data: -1, type: "Quarter" },
    CurrentHalfYear: { data: 0, type: "Half" },
    PreviousHalfYear: { data: -1, type: "Half" },
    CurrentYear: { data: 0, type: "Year" },
    PreviousYear: { data: -1, type: "Year" },
    NextYear: { data: 1, type: "Year" },
};

export function translateCoveredPeriod(
    coveredPeriod: CoveredPeriodKey
): string {
    switch (coveredPeriod) {
        case "None":
            return t("CoveredPeriod_None");
        case "CurrentMonth":
            return t("CoveredPeriod_0_Month");
        case "PreviousMonth":
            return t("CoveredPeriod_1_Month");
        case "TwoMonthsBeforeDueDate":
            return t("CoveredPeriod_2_Month");
        case "ThreeMonthsBeforeDueDate":
            return t("CoveredPeriod_3_Month");
        case "CurrentPeriod":
            return t("CoveredPeriod_0_TwoMonths");
        case "CurrentQuarter":
            return t("CoveredPeriod_0_Quarter");
        case "PreviousQuarter":
            return t("CoveredPeriod_1_Quarter");
        case "CurrentHalfYear":
            return t("CoveredPeriod_0_Half");
        case "PreviousHalfYear":
            return t("CoveredPeriod_1_Half");
        case "CurrentYear":
            return t("CoveredPeriod_0_Year");
        case "PreviousYear":
            return t("CoveredPeriod_1_Year");
        case "NextYear":
            return t("CoveredPeriod_1_Year_After");
        default:
            return assertNever(coveredPeriod);
    }
}

export function keyToCoveredPeriod(
    key: CoveredPeriodKey
): ControlCoveredPeriod | null {
    return COVERED_PERIOD_VALUES[key];
}

export function coveredPeriodToKey(
    value: ControlCoveredPeriod | null
): CoveredPeriodKey {
    for (const [keyStr, period] of Object.entries(COVERED_PERIOD_VALUES)) {
        const key = keyStr as CoveredPeriodKey;
        if (isEqual(value, period)) {
            return key;
        }
    }
    throw new Error(
        `Cannot find CoveredPeriodKey for type=${JSON.stringify(value)}`
    );
}
